import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  SimpleChanges,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatSelectModule } from '@angular/material/select';
import { ColorPickerModule } from '@iplab/ngx-color-picker';
import {
  FontSelectionModalComponent,
  FontOption,
} from './font-selection-modal/font-selection-modal.component';

export interface BrandColors {
  primary: string;
  heading: string;
  paragraph: string;
}

export interface FontSelection {
  heading: string;
  body: string;
}

export interface BrandingData {
  logo?: File;
  logoUrl?: string; // URL from API after upload
  logoSize?: 'small' | 'medium' | 'large';
  colors: BrandColors;
  fonts: FontSelection;
}

/**
 * BrandingPanelComponent provides a UI for configuring a website's branding including logo upload/preview, color selection with live previews, font selection via modal, and logo size options. It centralizes local preview state and emits events for both instant updates (e.g. color/logo) and finalized apply actions.
 * @component
 * @selector app-branding-panel
 * @example
 *   <app-branding-panel [brandingData]="sampleBranding" (brandingChange)="onBrandingChange($event)" (applyBranding)="onApplyBranding($event)"></app-branding-panel>
 * @input {BrandingData} brandingData - The initial/current branding configuration. Contains properties such as logo, logoUrl, logoSize, colors (primary/heading/paragraph), and fonts (heading/body). The component uses this to populate previews and editable controls.
 * @output {EventEmitter<BrandingData>} brandingChange - Emitted whenever a local branding preview is updated (colors, fonts, logoSize, etc.) to update the UI immediately without persisting to the API.
 * @output {EventEmitter<void>} setUpLater - Emitted when the user chooses to skip branding setup for now.
 * @output {EventEmitter<BrandingData>} applyBranding - Emitted to persist the current brandingData to the backend (e.g. when the user applies branding changes like fonts or finalizes settings).
 * @output {EventEmitter<void>} close - Emitted when the branding panel should be closed/cancelled.
 * @output {EventEmitter<{type: 'logo'|'color', data: Partial<BrandingData>}>} instantUpdate - Emitted for immediate, incremental updates used to trigger realtime API/editor updates (logo uploads and color changes).
 *
 * Key responsibilities:
 * - Manage logo upload and preview state (logoFile, logoPreview) with client-side validation (allowed image types: PNG/JPEG/GIF/SVG and max file size 20MB).
 * - Provide drag-and-drop and file-input handlers for logo selection.
 * - Create a data URL preview via createImagePreview and emit instantUpdate for logo changes.
 * - Offer color picking UI with open/close controls, normalize hex values, and emit both brandingChange and instantUpdate on color changes for live previews and immediate backend/editor updates.
 * - Provide font selection via a modal workflow: openFontModal, onFontSelected (applies and emits applyBranding for persistent changes) and utility getters getCurrentFontList / getCurrentSelectedFont.
 * - Allow logo size selection via onLogoSizeChange and reflect that in brandingChange for preview.
 * - Expose simple button actions: onSetUpLater, onApplyBranding (consolidates current logoFile into brandingData if needed), and onClose.
 *
 * Important methods:
 * - onFileSelected(event): handle file input selection, validate file, and delegate to createImagePreview.
 * - onDrop(event) / onDragOver(event): handle drag-and-drop file upload with same validation as file input.
 * - private createImagePreview(file): read file as DataURL, set logoPreview, update brandingData.logo, and emit instantUpdate for the logo.
 * - onColorChange(colorType, color): update brandingData.colors, emit brandingChange and instantUpdate for immediate application.
 * - openColorPicker(colorType) / closeColorPicker(): manage color picker state and current color selection.
 * - onColorPickerChange(): normalize and apply color from the picker, delegating to onColorChange.
 * - onFontChange(fontType, font): locally update fonts and emit brandingChange.
 * - openFontModal(fontType) / closeFontModal(): manage font modal state.
 * - onFontSelected(font): apply selected font to brandingData, emit brandingChange and applyBranding (persist fonts), then close modal.
 * - onLogoSizeChange(size): update logo size and emit brandingChange.
 * - onSetUpLater(), onApplyBranding(), onClose(): emit corresponding control events; onApplyBranding ensures logoFile is attached to brandingData before emitting.
 * - getContrastingTextColor(backgroundColor): utility to compute readable text color (black or white) based on WCAG luminance calculation.
 * - getFontFamilyString(fontName): return a CSS font-family string with sensible fallbacks.
 *
 * Lifecycle hooks:
 * - ngOnChanges(changes: SimpleChanges): if brandingData changes and contains a logoUrl, ensures the logoPreview is initialized from brandingData.logoUrl when no preview is present.
 *
 * Notes:
 * - File uploads are validated client-side for type and size (max 20MB). Upload errors are surfaced via uploadError state.
 * - instantUpdate is intended for immediate editor/backend updates (e.g. uploading a logo or applying color changes) while applyBranding is for explicit persist actions like font application or finalizing branding.
 */
@Component({
  selector: 'app-branding-panel',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatSelectModule,
    ColorPickerModule,
    FontSelectionModalComponent,
  ],
  templateUrl: './branding-panel.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./branding-panel.component.scss'],
})
export class BrandingPanelComponent implements OnChanges {
  @Input() brandingData: BrandingData = {
    logoSize: 'medium',
    colors: {
      primary: '#2C2C2C',
      heading: '#2C2C2C',
      paragraph: '#2C2C2C',
    },
    fonts: {
      heading: 'Sarabun',
      body: 'Open Sans',
    },
  };

  @Output() brandingChange = new EventEmitter<BrandingData>();
  @Output() setUpLater = new EventEmitter<void>();
  @Output() applyBranding = new EventEmitter<BrandingData>();
  @Output() close = new EventEmitter<void>();
  @Output() instantUpdate = new EventEmitter<{
    type: 'logo' | 'color';
    data: Partial<BrandingData>;
  }>(); // For instant logo/color updates

  // Logo upload state
  logoFile: File | null = null;
  logoPreview: string | null = null;
  isUploadingLogo = false;
  uploadError: string | null = null;
  selectedLogoSize: 'small' | 'medium' | 'large' = 'medium';

  // Color picker state
  isColorPickerOpen = false;
  currentColorType: keyof BrandColors | null = null;
  currentPickerColor = '#000000';

  ngOnChanges(changes: SimpleChanges): void {
    // If logoUrl exists in brandingData, show it as preview
    if (changes['brandingData'] && this.brandingData.logoUrl && !this.logoPreview) {
      this.logoPreview = this.brandingData.logoUrl;
    }
  }

  // Font selection modal state
  isFontModalOpen = false;
  currentFontType: 'heading' | 'body' | null = null;

  // Font options
  headingFonts: FontOption[] = [
    { name: 'Sora', value: 'Sora' },
    { name: 'Sarabun', value: 'Sarabun' },
    { name: 'Inter', value: 'Inter' },
    { name: 'Roboto', value: 'Roboto' },
    { name: 'Poppins', value: 'Poppins' },
    { name: 'Plus Jakarta Sans', value: 'Plus Jakarta Sans' },
    { name: 'Open Sans', value: 'Open Sans' },
    { name: 'Lato', value: 'Lato' },
    { name: 'Montserrat', value: 'Montserrat' },
    { name: 'Playfair Display', value: 'Playfair Display' },
    { name: 'Raleway', value: 'Raleway' },
  ];

  bodyFonts: FontOption[] = [
    { name: 'Open Sans', value: 'Open Sans' },
    { name: 'Inter', value: 'Inter' },
    { name: 'Roboto', value: 'Roboto' },
    { name: 'Lato', value: 'Lato' },
    { name: 'Sora', value: 'Sora' },
    { name: 'Sarabun', value: 'Sarabun' },
    { name: 'Poppins', value: 'Poppins' },
    { name: 'Plus Jakarta Sans', value: 'Plus Jakarta Sans' },
    { name: 'Montserrat', value: 'Montserrat' },
    { name: 'Raleway', value: 'Raleway' },
    { name: 'Nunito', value: 'Nunito' },
  ];

  // File upload - similar to add employee profile picture
  /**
   * Handle file selection from an input, validate image type and size, set the logo file, and create an image preview.
   * @example
   * nFileSelected({ target: { files: [file] } })
   * undefined
   * @param {{any}} {{event}} - File input change event containing the selected file at event.target.files[0].
   * @returns {{void}} No return value.
   **/
  onFileSelected(event: any): void {
    const file = event.target.files[0];
    if (file) {
      this.uploadError = null;

      // Validate file type
      const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/svg+xml'];
      if (!allowedTypes.includes(file.type)) {
        this.uploadError = 'Unsupported file format. Please upload a JPG, PNG, GIF, or SVG image.';
        return;
      }

      // Validate file size (max 20MB)
      const maxSize = 20 * 1024 * 1024; // 20MB
      if (file.size > maxSize) {
        this.uploadError = 'File exceeds 20MB size limit.';
        return;
      }

      this.isUploadingLogo = true;
      this.logoFile = file;
      // Don't set brandingData.logo here - createImagePreview will handle it and trigger instant update
      this.createImagePreview(file);
    }
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
  }

  /**
   * Handle files dropped onto the branding logo area, validate type and size, set upload state, and create an image preview.
   * @example
   * nDrop(sampleDragEvent)
   * undefined
   * @param {{DragEvent}} {{event}} - DragEvent from a drop action containing DataTransfer files.
   * @returns {{void}} No return value; performs validation, sets error state, assigns logo file, and initiates preview/upload.
   **/
  onDrop(event: DragEvent): void {
    event.preventDefault();
    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      const file = files[0];
      this.uploadError = null;

      // Validate file type
      const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/svg+xml'];
      if (!allowedTypes.includes(file.type)) {
        this.uploadError = 'Unsupported file format. Please upload a JPG, PNG, GIF, or SVG image.';
        return;
      }

      // Validate file size (max 20MB)
      const maxSize = 20 * 1024 * 1024; // 20MB
      if (file.size > maxSize) {
        this.uploadError = 'File exceeds 20MB size limit.';
        return;
      }

      this.isUploadingLogo = true;
      this.logoFile = file;
      // Don't set brandingData.logo here - createImagePreview will handle it and trigger instant update
      this.createImagePreview(file);
    }
  }

  /**
   * Creates a preview for an image File, updates component state, and emits an instant logo update.
   * @example
   * createImagePreview(file)
   * void
   * @param {File} file - Image file to generate a preview from and prepare for upload.
   * @returns {void} No return value.
   */
  private createImagePreview(file: File): void {
    const reader = new FileReader();

    reader.onload = (e: any) => {
      this.logoPreview = e.target.result;
      this.isUploadingLogo = false;

      // Emit instant update for logo (triggers immediate API call)
      this.brandingData.logo = file;
      this.logoFile = file;
      this.instantUpdate.emit({
        type: 'logo',
        data: {
          logo: file,
          logoUrl: this.brandingData.logoUrl, // Keep existing URL until new one is uploaded
        },
      });
    };

    reader.onerror = () => {
      this.isUploadingLogo = false;
      this.logoFile = null;
      this.logoPreview = null;
      this.uploadError = 'Error reading file. Please try again.';
    };

    reader.readAsDataURL(file);
  }

  // Color changes - instant update
  /**
   * Update a brand color and emit preview and instant update events.
   * @example
   * nColorChange('primary', '#ff0000')
   * undefined
   * @param {{keyof BrandColors}} {{colorType}} - The key of the color to update in the branding colors object (e.g., 'primary', 'accent').
   * @param {{string}} {{color}} - The new color value (e.g., '#ff0000' or 'rgb(255,0,0)') to set for the specified color key.
   * @returns {{void}} Nothing. Updates local branding data and emits both brandingChange and instantUpdate events.
   **/
  onColorChange(colorType: keyof BrandColors, color: string): void {
    this.brandingData.colors[colorType] = color;

    // Update local branding data for preview
    this.brandingChange.emit(this.brandingData);

    // Emit instant update to trigger immediate API call and editor update
    this.instantUpdate.emit({
      type: 'color',
      data: {
        colors: { ...this.brandingData.colors },
      },
    });
  }

  // Open color picker for a specific color type
  openColorPicker(colorType: keyof BrandColors): void {
    this.currentColorType = colorType;
    this.currentPickerColor = this.brandingData.colors[colorType];
    this.isColorPickerOpen = true;
  }

  // Close color picker
  closeColorPicker(): void {
    this.isColorPickerOpen = false;
    this.currentColorType = null;
  }

  // Handle color change from color picker
  // Since we're using two-way binding [(color)], this is called automatically when color changes
  onColorPickerChange(): void {
    if (this.currentColorType && this.currentPickerColor) {
      // Ensure hex color starts with #
      let hexColor = this.currentPickerColor;
      if (!hexColor.startsWith('#')) {
        hexColor = '#' + hexColor;
      }
      this.onColorChange(this.currentColorType, hexColor);
    }
  }

  // Font changes
  onFontChange(fontType: keyof FontSelection, font: string): void {
    this.brandingData.fonts[fontType] = font;
    this.brandingChange.emit(this.brandingData);
  }

  // Open font selection modal
  openFontModal(fontType: 'heading' | 'body'): void {
    this.currentFontType = fontType;
    this.isFontModalOpen = true;
  }

  // Close font selection modal
  closeFontModal(): void {
    this.isFontModalOpen = false;
    this.currentFontType = null;
  }

  // Handle font selection from modal - only updates when Apply is clicked
  /**
   * Handle a font selection for the currently active font type (heading or body): update local branding data, emit change and applyBranding events for immediate preview and API update, then close the font modal.
   * @example
   * nFontSelected('Roboto')
   * void
   * @param {string} font - Newly selected font family name.
   * @returns {void} Does not return a value; updates branding state, emits events, and closes the font modal.
   **/
  onFontSelected(font: string): void {
    if (this.currentFontType) {
      // Ensure we're updating the correct font type
      const previousFont = this.brandingData.fonts[this.currentFontType];

      // Only update if font actually changed
      if (font !== previousFont) {
        // Update local data for preview - ensure we update the correct type
        if (this.currentFontType === 'heading') {
          this.brandingData.fonts.heading = font;
        } else if (this.currentFontType === 'body') {
          this.brandingData.fonts.body = font;
        }

        // Emit change with updated fonts for immediate UI update
        this.brandingChange.emit({
          ...this.brandingData,
          fonts: {
            heading: this.brandingData.fonts.heading,
            body: this.brandingData.fonts.body,
          },
        });

        // Emit applyBranding to trigger API update for fonts
        // Note: This will update the API but should NOT close the branding panel
        this.applyBranding.emit({
          ...this.brandingData,
          fonts: {
            heading: this.brandingData.fonts.heading,
            body: this.brandingData.fonts.body,
          },
        });
      }

      // Close modal only - keep branding panel open
      this.closeFontModal();
    }
  }

  // Get current font list based on type
  getCurrentFontList(): FontOption[] {
    return this.currentFontType === 'heading' ? this.headingFonts : this.bodyFonts;
  }

  // Get current selected font
  getCurrentSelectedFont(): string {
    if (this.currentFontType === 'heading') {
      return this.brandingData.fonts.heading;
    } else if (this.currentFontType === 'body') {
      return this.brandingData.fonts.body;
    }
    return '';
  }

  // Logo size change
  onLogoSizeChange(size: 'small' | 'medium' | 'large'): void {
    this.selectedLogoSize = size;
    this.brandingData.logoSize = size;
    this.brandingChange.emit(this.brandingData);
  }

  // Button actions
  onSetUpLater(): void {
    this.setUpLater.emit();
  }

  /**
   * Apply current branding data, ensure a pending logo file is assigned to brandingData.logo when present, and emit the branding data via the applyBranding event.
   * @example
   * nApplyBranding()
   * undefined (emits brandingData object via this.applyBranding)
   * @param {void} none - No arguments.
   * @returns {void} No return value.
   **/
  onApplyBranding(): void {
    console.log('BrandingPanelComponent: onApplyBranding called');
    console.log('BrandingData being emitted:', {
      logo: this.brandingData.logo,
      logoUrl: this.brandingData.logoUrl,
      logoFile: this.logoFile,
      colors: this.brandingData.colors,
      fonts: this.brandingData.fonts,
    });

    // Ensure logo file is set if we have one
    if (this.logoFile && !this.brandingData.logo) {
      this.brandingData.logo = this.logoFile;
    }

    this.applyBranding.emit(this.brandingData);
  }

  onClose(): void {
    this.close.emit();
  }

  // Calculate contrasting text color based on background color
  /**
   * Returns a readable contrasting hex text color (#000000 or #FFFFFF) for a given background hex color.
   * @example
   * etContrastingTextColor('#00FF00')
   * '#000000'
   * @param {string} backgroundColor - Background color as a 6-digit hex string, with or without a leading '#'.
   * @returns {string} Contrasting text color as a hex string ('#000000' or '#FFFFFF').
   **/
  getContrastingTextColor(backgroundColor: string): string {
    // Remove # if present
    const hex = backgroundColor.replace('#', '');

    // Parse RGB values
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);

    // Calculate relative luminance using WCAG formula
    const getLuminance = (value: number) => {
      const normalized = value / 255;
      return normalized <= 0.03928
        ? normalized / 12.92
        : Math.pow((normalized + 0.055) / 1.055, 2.4);
    };

    const luminance =
      0.2126 * getLuminance(r) + 0.7152 * getLuminance(g) + 0.0722 * getLuminance(b);

    // If luminance is greater than 0.5, use black text, otherwise use white
    return luminance > 0.5 ? '#000000' : '#FFFFFF';
  }

  // Get font family string with fallback
  getFontFamilyString(fontName: string): string {
    if (!fontName) {
      return 'Inter, sans-serif';
    }
    // Return font name with fallback fonts
    return `"${fontName}", ${fontName.replace(/\s/g, '')}, sans-serif`;
  }
}
